import {
  createElement,
  CSSProperties,
  useRef,
  useEffect,
  useState,
  useCallback,
  AnimationEvent,
  forwardRef,
  Ref,
} from 'react';
import { Button, ButtonVariant } from '../../Button';
import { Flow } from '../../Layouts/Flow';
import { Heading, HeadingVariant } from '../../Heading';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { renderInlineCssVars, useDesignTokens, DesignTokens } from '../../../theme';
import { ReactKeyboardEvent, ReactMouseEvent } from '../../../react-synthetic-events';
import { Icon } from '../../Icon';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { isEventMarked } from '../../../marked-events';
import { postfixTestId, TestIdProps } from '../../../util/testId';
import { useIsRTL } from '../../../locale';
import { useDraggable } from './useDraggable';
import { Resizer } from '../../Resizable';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { SplitButton } from '../../SplitButton';
import { BusyStateIndicator } from '../../BusyStateIndicator';
import { setRef } from '../../../util/ref-util';
import {
  BaseDialogLayoutProps,
  DialogAccent,
  DialogAction,
  DialogDimensions,
  DialogSplitAction,
  DialogVariant,
} from '.';
import {
  Close,
  ErrorStatus,
  WarningStatus,
  InformationStatus,
  GrabberReorderToken,
} from '@sas/react-svg';
import { SVG } from '../../SVG';
import { convertCSSSizeToNumber } from '../../../util/dom';

// Import CSS last
import cx from 'classnames';
import cn from '../Dialog.css.json';
import '../Dialog.css';

const useHeaderId = createUseHtmlId('dialog-header');
const useAccentIconId = createUseHtmlId('dialog-accent-icon');

type DefaultButton = 'first' | 'second' | 'dismiss' | undefined;

interface DialogAnimation {
  // The ability to animate is validated in the onAnimationEnd event when opening. If this event is not triggered, then unmounting is not held up by that same event when closing.
  elementAcceptsAnimation: boolean;
  prevIsOpen: boolean;
  opening: boolean;
  closing: boolean;
}

export const BaseDialogLayout = forwardRef<HTMLDivElement, BaseDialogLayoutProps>(
  (props: BaseDialogLayoutProps, ref) => {
    const localize = useNCTextLocalizer('Dialog');
    const dt = useDesignTokens();
    const isRTL = useIsRTL();

    const role = props.role ?? 'dialog';
    const dialogHeaderId = useHeaderId();
    const accentIconId = useAccentIconId();
    const draggable = props.draggable ?? true;
    const resizable = props.resizable ?? false;

    const dialogRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const userHasInteracted = useRef(false);

    const ariaLabelledBy = props.accent ? `${accentIconId} ${dialogHeaderId}` : dialogHeaderId;

    const [transform, setTransform] = useState({ x: 0, y: 0 });

    // If animations are enabled, we can prepare the browser for executing that animation by
    // applying `will-change` styles when we believe the animation is about to run.
    const [GPUAcceleration, setGPUAcceleration] = useState(!props.hideAnimation);

    const [size, setSize] = useState<DialogDimensions>(props.size ?? {});

    const defaultMinHeight = convertCSSSizeToNumber('10rem');

    const maxWidthInPixels = convertCSSSizeToNumber(size.maxWidth);
    const maxHeightInPixels = convertCSSSizeToNumber(size.maxHeight);

    const { viewportWidth: vw, viewportHeight: vh } = getViewportDimensions();

    size.maxWidth = maxWidthInPixels === 0 ? vw : Math.min(vw, maxWidthInPixels);

    size.maxHeight = maxHeightInPixels === 0 ? vh : Math.min(vh, maxHeightInPixels);

    const minHeightInPixels = Math.max(
      convertCSSSizeToNumber(props.size?.minHeight),
      convertCSSSizeToNumber(props.style?.minHeight),
      convertCSSSizeToNumber(size.minHeight)
    );
    size.minHeight =
      minHeightInPixels === 0
        ? Math.min(defaultMinHeight, size.maxHeight)
        : Math.min(minHeightInPixels, size.maxHeight);

    // reflects the current transform in terms of percentage.
    const transformInPercentage = useRef(isRTL ? { x: 50, y: -50 } : { x: -50, y: -50 });
    // used to re-adjust dialog position in certain condition when the dialog's exact dimensions
    // are known in a delayed fashion.
    const couldNeedAdjustment = useRef(false);

    function getViewportDimensions() {
      return {
        viewportWidth: document.documentElement.clientWidth - 64 || 0,
        viewportHeight: document.documentElement.clientHeight - 64 || 0,
      };
    }

    function getCurrentTransformValueInPercentage(): typeof transform {
      const dialogRect = dialogRef.current!.getBoundingClientRect();
      const { viewportWidth, viewportHeight } = getViewportDimensions();

      return {
        x: isRTL
          ? Math.round(((dialogRect.right - 32 - viewportWidth / 2) / dialogRect.width) * 100)
          : Math.round(((dialogRect.left - 32 - viewportWidth / 2) / dialogRect.width) * 100),
        y: Math.round(((dialogRect.top - 32 - viewportHeight / 2) / dialogRect.height) * 100),
      };
    }

    const isClipping = useCallback(
      (_transform: typeof transform) => {
        const dialogRect = dialogRef.current!.getBoundingClientRect();
        const { viewportWidth, viewportHeight } = getViewportDimensions();

        const absoluteX = viewportWidth / 2 + _transform.x;
        const absoluteY = viewportHeight / 2 + _transform.y;

        const absolutePositionOutOfBounds =
          absoluteX < 0 || absoluteX > viewportWidth || absoluteY < 0 || absoluteY > viewportHeight;

        return (
          absolutePositionOutOfBounds ||
          (isRTL
            ? absoluteX - dialogRect.width < 0
            : absoluteX + dialogRect.width >= viewportWidth) ||
          absoluteY + dialogRect.height >= viewportHeight
        );
      },
      [isRTL]
    );

    const style = {
      ...renderInlineCssVars(getDialogContentTokens(dt, props.contentPadding), 'dialog-content'),
      ...renderInlineCssVars(
        getDialogHeaderTokens(dt, props.accent, props.variant),
        'dialog-header'
      ),
      ...renderInlineCssVars(
        {
          s: dt.spacing.core.s,
          m: dt.spacing.core.m,
          l: dt.spacing.core.l,
          'squish-xl': dt.spacing.insetSquish.xl.padding,
          none: dt.spacing.core.none,
        },
        'dialog-spacing'
      ),
      ...renderInlineCssVars(
        {
          'anim-dur-enter': dt.animation.duration.enter.m,
          'anim-tf-enter': dt.animation.timingFunction.enter.m,
          'anim-dur-exit': dt.animation.duration.exit.m,
          'anim-tf-exit': dt.animation.timingFunction.exit.m,
          'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
          'font-weight-thick': dt.enableNovaTokens
            ? dt.fontNova.weight.thick
            : dt.font.weight.thick,
        },
        'dialog'
      ),
      // Round translate values to prevent fractional pixel translations.
      transform: `translate3d(${Math.round(transform.x)}px,${Math.round(transform.y)}px, 0px)`,
    };

    let headerIcon;
    switch (props.accent) {
      case 'alert':
        headerIcon = ErrorStatus;
        break;
      case 'warning':
        headerIcon = WarningStatus;
        break;
      default:
        headerIcon = InformationStatus;
    }

    let buttonVariant = ButtonVariant.Icon;
    switch (dt.themeVariant) {
      case 'dark':
      case 'high-contrast':
        buttonVariant = ButtonVariant.Icon;
        break;
      default:
        buttonVariant =
          props.variant === 'alternate' ? ButtonVariant.Icon : ButtonVariant.PrimaryIcon;
        break;
    }

    // Apply these events when we believe the Dialog is about to unmount.
    // - when the user hovers over the close button
    // - when the user hovers over the action buttons that dismiss the Dialog.
    const GPUAccelerationEvents = props.gpuAccelerateAnimation
      ? {
          onMouseEnter: () => {
            setGPUAcceleration(!props.hideAnimation);
          },
          onMouseLeave: () => {
            setGPUAcceleration(false);
          },
        }
      : undefined;

    const closeIconButton =
      !props.hideCloseIcon && props.dismissAction ? (
        <div {...GPUAccelerationEvents}>
          <Button
            testId={postfixTestId(props.testId, 'closeIconBtn')}
            className={cn.closeIcon}
            variant={buttonVariant}
            icon={{
              icon: Close,
              tokens: () => {
                return {
                  fg: 'var(--dialog-header-fg)',
                };
              },
            }}
            ariaLabel={localize('dialog.close.aria', { title: props.title })}
            onPress={props.dismissAction?.onPress}
            style={
              props.variant === 'alternate' &&
              (dt.themeVariant === 'light' || dt.themeVariant === 'high-contrast')
                ? { ['--button-focus-outline' as any]: 'var(--focus-dark-bg)' }
                : undefined
            }
          />
        </div>
      ) : null;

    // Make sure we only have one default button
    let defaultButton: DefaultButton = undefined;
    if (props.firstAction?.isDefault) {
      defaultButton = 'first';
    } else if (props.secondAction?.isDefault) {
      defaultButton = 'second';
    } else if (props.dismissAction?.isDefault) {
      defaultButton = 'dismiss';
    }

    const handleKeyDown = (e: ReactKeyboardEvent) => {
      if (e.defaultPrevented) return;

      if (props.busyStateIndicatorProps?.active) {
        // No key should be handled if the current dialog is busy
        e.preventDefault();
        return;
      }

      userHasInteracted.current = true;

      if (e.key === 'Escape') {
        e.stopPropagation();
        props.dismissAction?.onPress?.();
      }

      if (!isEventMarked(e) && e.key === 'Enter') {
        if (props.firstAction?.isDefault && !props.firstAction?.disabled) {
          e.preventDefault();
          props.firstAction.onPress?.();
        } else if (props.secondAction?.isDefault && !props.secondAction?.disabled) {
          e.preventDefault();
          props.secondAction.onPress?.();
        } else if (props.dismissAction?.isDefault && !props.dismissAction?.disabled) {
          e.preventDefault();
          props.dismissAction.onPress?.();
        }
      }
    };

    const { startDragging, onArrowKeyDrag, calculateBounds } = useDraggable({
      ref: dialogRef,
      initialPosition: transform,
      dragCancelHandle: [cn.closeIcon, cn.dialogTitle],
      onDrag: (position) => {
        transformInPercentage.current = getCurrentTransformValueInPercentage();
        setTransform(position);
      },
      onDragEnd: (position) => {
        transformInPercentage.current = getCurrentTransformValueInPercentage();
        setTransform(position);
        updateDialogDims(position, true);
      },
    });

    const dragIcon = draggable ? (
      <div
        role="img"
        tabIndex={0}
        className={cn.dragIconWrapper}
        onKeyDown={onArrowKeyDrag}
        data-testid={postfixTestId(props.testId, 'dragIcon')}
        title={localize('dialog.dragIcon.label')}
        aria-label={localize('dialog.dragIcon.aria')}
      >
        <Icon
          tokens={() => ({
            fg: 'var(--dialog-header-fg)',
          })}
          className={cn.dragIcon}
          icon={GrabberReorderToken}
          tooltip=""
          ariaLabel=""
        />
      </div>
    ) : null;

    const resizer = resizable ? (
      <Resizer
        resizableElementRef={dialogRef}
        onResize={(newSize) => {
          setSize((prevState) => {
            return { ...prevState, ...newSize };
          });
          draggable && calculateBounds();
        }}
        testId={postfixTestId(props.testId, 'resizeGripIcon')}
        tooltip={localize('dialog.gripIcon.label')}
        ariaLabel={localize('dialog.gripIcon.aria')}
        {...GPUAccelerationEvents}
      />
    ) : null;

    const getTransformXYPosition = useCallback(() => {
      if (!dialogRef.current) return;

      const node = dialogRef.current;
      const rect = node.getBoundingClientRect();

      return {
        x: isRTL ? rect?.width / 2 : -(rect?.width / 2),
        y: -(rect?.height / 2),
      };
    }, [isRTL]);

    const [setDialogRef] = useResizeObserverRef(() => {
      const newTransform = getTransformXYPosition();

      if (
        (!userHasInteracted.current || couldNeedAdjustment.current) &&
        newTransform &&
        JSON.stringify(transform) !== JSON.stringify(newTransform)
      ) {
        transformInPercentage.current = isRTL ? { x: 50, y: -50 } : { x: -50, y: -50 };
        setTransform(newTransform);

        updateDialogDims(newTransform);
        couldNeedAdjustment.current = false;
        return;
      }
      couldNeedAdjustment.current = false;
      transformInPercentage.current = getCurrentTransformValueInPercentage();
    });

    useEffect(() => {
      if (!dialogRef.current || !headerRef.current || !contentRef.current) return;

      const dialog = dialogRef.current;
      const header = headerRef.current;
      const content = contentRef.current;

      const currentMinWidth = dialog.style.minWidth;
      const currentMaxWidth = dialog.style.maxWidth;
      const currentWidth = dialog.style.width;

      // Get the initial min width in pixels since it could be set to rem/%/calc()
      dialog.style.width = '0';
      const currentMinWidthAsPx = dialog.offsetWidth;
      dialog.style.width = '';

      // Hide the dialog sections not related to the footer. remove the min/max widths of the
      // dialog so the footer can render at it's natural width.
      dialog.style.maxWidth = 'none';
      dialog.style.minWidth = '0';
      header.style.display = 'none';
      content.style.display = 'none';

      // Get the width of the dialog (which is only the footer)
      const offsetWidth = dialog.offsetWidth;

      // Reset the temporary styles
      dialog.style.width = currentWidth;
      dialog.style.maxWidth = currentMaxWidth;
      dialog.style.minWidth = currentMinWidth;
      header.style.display = '';
      content.style.display = '';

      // Update the minWidth if the footer is wider than the current minWidth
      let newMinWidth = currentMinWidthAsPx;
      if (offsetWidth > newMinWidth) {
        newMinWidth = offsetWidth;
        // Update the state
        setSize((prevSize) => {
          return { ...prevSize, minWidth: newMinWidth };
        });
      }
    }, [size.minWidth]);

    // updates the size maxWidth and maxHeight based on the set transform value.
    const updateDialogDims = useCallback(
      (_transform: typeof transform, isDragged = false) => {
        const { viewportWidth, viewportHeight } = getViewportDimensions();

        setSize((prevSize) => {
          let newMaxWidth = isRTL
            ? viewportWidth / 2 + _transform.x
            : viewportWidth - (viewportWidth / 2 + _transform.x);
          const maxWidthFromSizeProp = convertCSSSizeToNumber(props.size?.maxWidth);
          if (maxWidthFromSizeProp > 0) {
            newMaxWidth = Math.min(maxWidthFromSizeProp, newMaxWidth);
          }
          const maxWidthFromStyleProp = convertCSSSizeToNumber(props.style?.maxWidth);
          if (maxWidthFromStyleProp > 0) {
            newMaxWidth = Math.min(maxWidthFromStyleProp, newMaxWidth);
          }

          let newMaxHeight = viewportHeight - (viewportHeight / 2 + _transform.y);
          const maxHeightFromSizeProp = convertCSSSizeToNumber(props.size?.maxHeight);
          if (maxHeightFromSizeProp > 0) {
            newMaxHeight = Math.min(maxHeightFromSizeProp, newMaxHeight);
          }
          const maxHeightFromStyleProp = convertCSSSizeToNumber(props.style?.maxHeight);
          if (maxHeightFromStyleProp > 0) {
            newMaxHeight = Math.min(maxHeightFromStyleProp, newMaxHeight);
          }

          if (prevSize.maxHeight === newMaxHeight && prevSize.maxWidth === newMaxWidth) {
            return prevSize;
          }

          if (
            !isDragged &&
            transformInPercentage.current.x === (isRTL ? 50 : -50) &&
            transformInPercentage.current.y === -50
          ) {
            couldNeedAdjustment.current = true;
          }
          return {
            ...prevSize,
            maxWidth: newMaxWidth,
            maxHeight: newMaxHeight,
          };
        });
      },
      [
        isRTL,
        props.size?.maxHeight,
        props.size?.maxWidth,
        props.style?.maxWidth,
        props.style?.maxHeight,
      ]
    );

    // Verifies if the current dialog position will not cause any clipping
    // following browser resize or zoom operation. If it clips, we position
    // the dialog centrally.
    const updateDialogPositionAndDims = useCallback(() => {
      if (dialogRef.current) {
        const { x: xPercent, y: yPercent } = transformInPercentage.current;

        const dialogRect = dialogRef.current.getBoundingClientRect();

        // Check added to handle NOVACOMMONS-11961
        if (dialogRect.width <= 0 || dialogRect.height <= 0) {
          return;
        }

        const transform = {
          x: (xPercent / 100) * dialogRect.width,
          y: (yPercent / 100) * dialogRect.height,
        };

        let dialogWidth = dialogRect.width;
        let dialogHeight = dialogRect.height;

        if (isClipping(transform)) {
          transformInPercentage.current = isRTL ? { x: 50, y: -50 } : { x: -50, y: -50 };
          const { viewportWidth, viewportHeight } = getViewportDimensions();

          if (dialogWidth > viewportWidth) {
            dialogWidth = viewportWidth;
          }

          if (dialogHeight > viewportHeight) {
            dialogHeight = viewportHeight;
          }

          transform.x = (isRTL ? 0.5 : -0.5) * dialogWidth;
          transform.y = -0.5 * dialogHeight;
        }

        setTransform((prevPosition) => {
          if (prevPosition.x !== transform.x || prevPosition.y !== transform.y) {
            return transform;
          }
          return prevPosition;
        });

        updateDialogDims(transform);
      }
    }, [updateDialogDims, isClipping, isRTL]);

    useEffect(() => {
      updateDialogPositionAndDims();

      const fn = () => {
        updateDialogPositionAndDims();
      };
      window.addEventListener('resize', fn);
      return () => {
        window.removeEventListener('resize', fn);
      };
    }, [updateDialogPositionAndDims]);

    function isInitialFocusConfigured() {
      return !!(
        props.firstAction?.initialFocus ||
        props.secondAction?.initialFocus ||
        props.dismissAction?.initialFocus
      );
    }

    const [{ prevIsOpen, opening, closing }, setAnimation] = useState<DialogAnimation>({
      elementAcceptsAnimation: false,
      prevIsOpen: props.isOpen,
      opening: !props.hideAnimation,
      closing: false,
    });

    if (props.isOpen !== prevIsOpen) {
      setAnimation((prev) => ({
        ...prev,
        prevIsOpen: props.isOpen,
        opening: !props.hideAnimation && !prevIsOpen,
        closing: !props.hideAnimation && prev.elementAcceptsAnimation && prevIsOpen,
      }));
    }

    return props.isOpen || closing ? (
      <div
        data-testid={postfixTestId(props.testId, 'dialog')}
        ref={(element) => {
          dialogRef.current = element;
          setDialogRef(element);
          setRef(ref, element);
        }}
        role={role}
        aria-labelledby={ariaLabelledBy}
        className={cx(cn.dialog, props.className)}
        style={{ ...style, ...props.style, ...size }}
        aria-describedby={props.ariaDescribedBy}
        onKeyDown={handleKeyDown}
        onMouseDown={() => {
          userHasInteracted.current = true;
        }}
      >
        <div
          className={cx(cn.dialogInner, opening && cn.opening, closing && cn.closing)}
          role={props.isModal === false ? 'region' : undefined}
          data-landmark-label={
            props.isModal === false ? props.landmarkLabel ?? props.title : undefined
          }
          style={{
            minHeight: size.minHeight,
            ...(GPUAcceleration ? { willChange: 'transform, opacity' } : undefined),
          }}
          onAnimationEnd={(event: AnimationEvent) => {
            if (event.target === event.currentTarget) {
              setGPUAcceleration(false);
              if (opening) {
                setAnimation((prev) => ({
                  ...prev,
                  elementAcceptsAnimation: true,
                  opening: false,
                }));
              }
              if (closing) {
                setAnimation((prev) => ({
                  ...prev,
                  prevIsOpen: false,
                  opening: false,
                  closing: false,
                }));
              }
            }
          }}
        >
          <BusyStateIndicator
            {...props.busyStateIndicatorProps}
            fillParentContainer
            containerClassName={cn.busyStateIndicatorContainer}
          >
            <div
              ref={headerRef}
              data-testid={postfixTestId(props.testId, 'header')}
              className={cx(cn.dialogHeader, draggable && cn.dragCursor)}
              onMouseDown={(e: ReactMouseEvent) => {
                if (draggable) {
                  startDragging(e);
                }
                (document.activeElement as HTMLElement)?.blur();
                e.preventDefault();
              }}
            >
              {props.accent ? (
                <div className={cn.accent}>
                  <div className={cn.iconContainer}>
                    <Icon
                      id={accentIconId}
                      icon={headerIcon}
                      tokens={() => ({
                        fg: dt.c.colorTextInverse,
                      })}
                    />
                  </div>
                </div>
              ) : null}
              {props.headerImage ? (
                <SVG className={cn.headerImage} icon={props.headerImage} />
              ) : null}
              {props.headerLogo ? <SVG className={cn.headerLogo} icon={props.headerLogo} /> : null}
              <Heading
                testId={postfixTestId(props.testId, 'heading')}
                id={dialogHeaderId}
                level={props.isModal ? 1 : 2}
                className={cx(cn.dialogTitle, cn.noDragCursor)}
                variant={HeadingVariant.PrimaryLight}
                wrapText={props.titleWrapText}
              >
                {props.title}
              </Heading>
              {closeIconButton}
            </div>
            <section
              ref={contentRef}
              className={cn.dialogContent}
              {...(!isInitialFocusConfigured() && { ['data-autofocus-inside']: true })}
            >
              {props.children}
            </section>
            {props.subContentFooterControl ? (
              <div
                className={cn.dialogSubFooter}
                data-testid={postfixTestId(props.testId, 'subContentFooter')}
                {...(!isInitialFocusConfigured() && { ['data-autofocus-inside']: true })}
              >
                {props.subContentFooterControl}
              </div>
            ) : null}

            {props.footerControls ||
            props.firstAction ||
            props.secondAction ||
            props.dismissAction ? (
              <div
                data-testid={postfixTestId(props.testId, 'footer')}
                className={cn.dialogFooter}
                {...(defaultButton === undefined &&
                  !isInitialFocusConfigured() && { ['data-autofocus-inside']: true })}
              >
                <div
                  className={cn.dialogFooterControls}
                  data-autofocus-inside
                  data-testid={postfixTestId(props.testId, 'footerControls')}
                >
                  {props.footerControls}
                </div>
                <Flow spacing="s" className={cn.dialogFooterButtons} {...GPUAccelerationEvents}>
                  {props.firstAction ? (
                    <DialogActionButton
                      buttonRef={props.firstActionRef}
                      action={props.firstAction}
                      isDefault={defaultButton === 'first'}
                      testId={postfixTestId(props.testId, 'firstButton')}
                    />
                  ) : null}
                  {props.secondAction ? (
                    <DialogActionButton
                      buttonRef={props.secondActionRef}
                      action={props.secondAction}
                      isDefault={defaultButton === 'second'}
                      testId={postfixTestId(props.testId, 'secondButton')}
                    />
                  ) : null}
                  {props.dismissAction ? (
                    <DialogActionButton
                      buttonRef={props.dismissActionRef}
                      action={props.dismissAction}
                      isDefault={defaultButton === 'dismiss'}
                      testId={postfixTestId(props.testId, 'dismissButton')}
                    />
                  ) : null}
                </Flow>
              </div>
            ) : null}
            {resizer}
            {dragIcon}
          </BusyStateIndicator>
        </div>
      </div>
    ) : null;
  }
);

const getDialogHeaderTokens = (
  dt: DesignTokens,
  accent?: DialogAccent,
  variant?: DialogVariant
) => {
  const alternate = variant === 'alternate';
  const dialogBg = alternate ? dt.c.colorSecondary : dt.c.colorUiPop;
  if (!accent) {
    if (dt.enableNovaTokens) {
      switch (dt.themeVariant) {
        case 'dark':
          return {
            bg: dialogBg,
            fg: dt.c.colorTextDefault,
            'drag-icon-focus': 'var(--focus-light-bg)',
          };
        case 'high-contrast':
          return {
            bg: dialogBg,
            fg: alternate ? dt.c.colorTextInverse : dt.c.colorTextDefault,
            'drag-icon-focus': alternate ? 'var(--focus-dark-bg)' : 'var(--focus-light-bg)',
          };
        default:
          return {
            bg: dialogBg,
            fg: dt.c.colorTextInverse,
            'drag-icon-focus': 'var(--focus-dark-bg)',
          };
      }
    } else {
      switch (dt.baseReportThemeName) {
        case 'highcontrast2018':
        case 'highcontrast2017':
          return {
            bg: dt.c.black,
            fg: dt.c.white,
            'drag-icon-focus': 'var(--focus-light-bg)',
          };
      }
      switch (dt.themeVariant) {
        case 'dark':
          return {
            bg: dt.c.p_2,
            fg: dt.c.white,
            'drag-icon-focus': 'var(--focus-light-bg)',
          };
        case 'high-contrast':
          return {
            bg: dt.c.n_4, // Livingston says n_5, but VA uses n_4
            fg: dt.c.white,
            'drag-icon-focus': 'var(--focus-light-bg)',
          };
        default:
          return {
            bg: dt.c.p,
            fg: dt.c.white,
            'drag-icon-focus': 'var(--focus-dark-bg)',
          };
      }
    }
  }

  let accentColor;
  switch (accent) {
    case 'alert':
      accentColor = dt.enableNovaTokens ? dt.c.colorAlert : dt.c.alert;
      break;
    case 'warning':
      accentColor = dt.enableNovaTokens ? dt.c.colorWarning : dt.c.warning;
      break;
    default:
      accentColor = dt.enableNovaTokens ? dt.c.colorInfo : dt.c.informational;
  }

  return {
    accent: accentColor,
    bg: 'transparent',
    fg: accentColor,
    'drag-icon-focus': 'var(--focus-light-bg)',
  };
};

const getDialogContentTokens = (dt: DesignTokens, contentPadding: CSSProperties['padding']) => {
  let tokens;
  if (dt.enableNovaTokens) {
    tokens = {
      ...dt.backgroundsDepthNova.large,
      bd: `1px solid ${dt.c.colorUiBorder}`,
      fg: dt.c.colorTextDefault,
    };
  } else {
    switch (dt.themeVariant) {
      case 'high-contrast':
        tokens = {
          ...dt.backgroundsDepth.large,
          bd: `1px solid ${dt.backgroundsDepth.large.bd}`,
          fg: dt.c.white,
        };
        break;
      case 'dark':
        tokens = { ...dt.backgroundsDepth.large, bd: 'none', fg: dt.c.white };
        break;
      default:
        tokens = { ...dt.backgroundsDepth.large, bd: 'none', fg: dt.c.n_1 };
        break;
    }
  }

  // If contentPadding is provided as a string for the CSS padding shorthand (2, 3, or 4 values), we need to isolate the left padding
  // so it can be applied to the footer for content alignment.

  let startPadding = contentPadding;
  if (contentPadding && typeof contentPadding === 'string' && contentPadding.includes(' ')) {
    const paddingValues = contentPadding.split(' ');
    if (paddingValues.length < 4) {
      startPadding = paddingValues[1];
    } else {
      startPadding = paddingValues[3];
    }
  }

  tokens = {
    ...tokens,
    pd: contentPadding ?? dt.spacing.core.xl,
    'start-pd': startPadding,
  };
  return tokens;
};

const isDialogSplitAction = (
  action: DialogAction | DialogSplitAction
): action is DialogSplitAction => {
  return (action as DialogSplitAction).items !== undefined;
};

interface DialogActionButtonProps extends TestIdProps {
  action: DialogAction | DialogSplitAction;
  isDefault: boolean;
  buttonRef?: Ref<HTMLButtonElement | HTMLDivElement>;
}

const DialogActionButton = (props: DialogActionButtonProps) => {
  const action = props.action;
  const buttonRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null);
  return isDialogSplitAction(action) ? (
    <SplitButton
      ref={(element) => {
        buttonRef.current = element;
        setRef(props.buttonRef, element);
      }}
      testId={props.testId}
      variant={props.isDefault ? ButtonVariant.Primary : ButtonVariant.Standard}
      text={action.text}
      onPress={action.onPress}
      autoFocus={action.isDefault || action.initialFocus ? true : undefined}
      disabled={action.disabled}
      items={action.items}
      onItemSelected={action.onItemSelected}
    />
  ) : (
    <Button
      ref={(element) => {
        buttonRef.current = element;
        setRef(props.buttonRef, element);
      }}
      testId={props.testId}
      variant={props.isDefault ? ButtonVariant.Primary : ButtonVariant.Standard}
      onPress={action.onPress}
      data-autofocus={action.isDefault || action.initialFocus ? true : undefined}
      disabled={action.disabled}
      text={action.text}
      ariaLabel={action.ariaLabel}
    />
  );
};
